import { useQuery } from '@tanstack/react-query'
import { adminService } from '@/lib/api/services/admin.service'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { MousePointerClick, Eye, Target, Hash, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

interface SEOAnalyticsProps {
  startDate?: string
  endDate?: string
}

function StatCard({
  icon: Icon,
  value,
  label,
  tooltip,
  iconBgColor = 'bg-[rgba(70,105,237,0.10)]',
  iconColor = 'text-[#4669ed]',
}: {
  icon: React.ComponentType<{ className?: string }>
  value: string | number
  label: string
  tooltip: string
  iconBgColor?: string
  iconColor?: string
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="bg-white rounded-3xl border border-slate-200 p-6 flex flex-col items-center">
            <div className={cn('w-14 h-14 rounded-full flex items-center justify-center mb-3', iconBgColor)}>
              <Icon className={cn('w-6 h-6', iconColor)} />
            </div>
            <div className="text-4xl font-semibold text-slate-900">{value}</div>
            <div className="text-sm text-slate-500 mt-2">{label}</div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function SEOAnalytics({ startDate, endDate }: SEOAnalyticsProps) {
  const start = startDate || new Date(Date.now() - 28 * 86400000).toISOString().split('T')[0]
  const end = endDate || new Date(Date.now() - 86400000).toISOString().split('T')[0]

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['seo-overview', start, end],
    queryFn: () => adminService.getSEOOverview(start, end),
    staleTime: 5 * 60 * 1000,
  })

  const { data: trends = [], isLoading: trendsLoading } = useQuery({
    queryKey: ['seo-trends', start, end],
    queryFn: () => adminService.getSEOTrends(start, end),
    staleTime: 5 * 60 * 1000,
  })

  const { data: topQueries = [], isLoading: queriesLoading } = useQuery({
    queryKey: ['seo-queries', start, end],
    queryFn: () => adminService.getSEOQueries(start, end, 20),
    staleTime: 5 * 60 * 1000,
  })

  const { data: topPages = [], isLoading: pagesLoading } = useQuery({
    queryKey: ['seo-pages', start, end],
    queryFn: () => adminService.getSEOPages(start, end, 20),
    staleTime: 5 * 60 * 1000,
  })

  const isLoading = overviewLoading || trendsLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        <span className="ml-3 text-slate-500">Loading SEO data...</span>
      </div>
    )
  }

  const noData = !overview || (overview.clicks === 0 && overview.impressions === 0)

  return (
    <div className="space-y-8">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={MousePointerClick}
          value={overview?.clicks?.toLocaleString() || '0'}
          label="Total Clicks"
          tooltip="Number of clicks from Google Search results to your site"
          iconBgColor="bg-[rgba(34,197,94,0.10)]"
          iconColor="text-green-600"
        />
        <StatCard
          icon={Eye}
          value={overview?.impressions?.toLocaleString() || '0'}
          label="Total Impressions"
          tooltip="Number of times your pages appeared in Google Search results"
          iconBgColor="bg-[rgba(59,130,246,0.10)]"
          iconColor="text-blue-600"
        />
        <StatCard
          icon={Target}
          value={overview?.ctr ? `${(overview.ctr * 100).toFixed(1)}%` : '0%'}
          label="Average CTR"
          tooltip="Click-through rate: percentage of impressions that resulted in a click"
          iconBgColor="bg-[rgba(168,85,247,0.10)]"
          iconColor="text-purple-600"
        />
        <StatCard
          icon={Hash}
          value={overview?.position ? overview.position.toFixed(1) : '0'}
          label="Avg Position"
          tooltip="Average ranking position in Google Search results (lower is better)"
          iconBgColor="bg-[rgba(249,115,22,0.10)]"
          iconColor="text-orange-600"
        />
      </div>

      {noData && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center">
          <p className="text-blue-800 font-medium mb-1">No search data yet</p>
          <p className="text-blue-600 text-sm">
            Google Search Console data typically takes 2-3 days to appear after verification.
            Your site was just verified today. Check back in a few days.
          </p>
        </div>
      )}

      {/* Trends Chart */}
      {trends.length > 0 && (
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Search Performance Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trends} margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <RechartsTooltip
                labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#22c55e" strokeWidth={2} dot={false} name="Clicks" />
              <Line yAxisId="right" type="monotone" dataKey="impressions" stroke="#3b82f6" strokeWidth={2} dot={false} name="Impressions" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Queries and Top Pages side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Queries */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Top Search Queries</h3>
          {queriesLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : topQueries.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Query</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">Pos.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topQueries.map((q, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm max-w-[200px] truncate">{q.query}</TableCell>
                      <TableCell className="text-right text-sm">{q.clicks}</TableCell>
                      <TableCell className="text-right text-sm">{q.impressions}</TableCell>
                      <TableCell className="text-right text-sm">{q.position.toFixed(1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center py-8">No query data available yet</p>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-3xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Top Pages</h3>
          {pagesLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : topPages.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">Pos.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topPages.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-sm max-w-[200px] truncate">
                        {p.page.replace('https://aiexpertnetwork.com', '')}
                      </TableCell>
                      <TableCell className="text-right text-sm">{p.clicks}</TableCell>
                      <TableCell className="text-right text-sm">{p.impressions}</TableCell>
                      <TableCell className="text-right text-sm">{p.position.toFixed(1)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center py-8">No page data available yet</p>
          )}
        </div>
      </div>
    </div>
  )
}
